import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useParams } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';

import {
    BarController,
    BarElement,
    Chart,
    DoughnutController,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    ScatterController,
    Tooltip,
    Legend,
} from 'chart.js';

import CommitmentService from './CommitmentService';

import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import {
    getDomainValueForCode,
    getFlexColDomainValueForCode,
    isEmpty,
    isSuperAdmin,
} from '../../../library/Core/SmartFunctions';

Chart.register(
    BarController,
    BarElement,
    DoughnutController,
    ArcElement,
    ScatterController,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);

const CommitmentTracker = () => {
    const params = useParams();
    const { sessionState } = useContext(SessionContext);
    const { globalState } = useContext(GlobalContext);

    /* ---------------- LOADER DATA ---------------- */

    const { formConfig, data, routeInfo, domain: loadedDomain } =
        (useLoaderData() as State) || ({} as State);

    /* ---------------- INSTITUTE / CAMPUS RESOLUTION ---------------- */

    // Three modes:
    //   - "url-group"  : URL has :instituteId AND that institute has published
    //                    children (parentCode === urlInstituteId) → it's a
    //                    group institute. Show the campus dropdown with the
    //                    parent + every child as options. Default selection
    //                    is the URL id (not "all") so the page initially
    //                    reflects what the admin clicked into.
    //   - "url-single" : URL has :instituteId but no children → single
    //                    institute view, no dropdown.
    //   - "management" : No URL param. Show dropdown when the user is a
    //                    super-admin OR belongs to a multi-campus session.
    //                    Default selection is every published campus.
    const urlInstituteId = params.instituteId ? String(params.instituteId) : '';
    const sessionInstitutes: any[] = Array.isArray(sessionState?.institutes)
        ? sessionState!.institutes
        : [];

    // Prefer the loader-resolved domain over globalState.domain — on hard
    // refresh the loader resolves before render but globalState.domain may
    // not be hydrated yet.
    const resolvedDomain: any = loadedDomain ?? globalState?.domain;

    // Build the campus list. Source depends on mode:
    //   - URL mode: resolve the URL institute's `institute_group_id` from its
    //     INSTITUTE_DOMAIN.flexCol (same key AdminInstituteSelection uses to
    //     scope campuses to a group). Then every published row sharing that
    //     institute_group_id is a campus in the group. This handles both
    //     "URL is the group root" and "URL is one of the sibling campuses"
    //     uniformly — parentCode/child detection doesn't, because siblings
    //     don't have parentCode === each other.
    //   - management mode: published rows resolved from sessionState.institutes
    const campuses: Array<{ label: string; value: string }> = useMemo(() => {
        const domainRows: any[] =
            resolvedDomain?.get?.('INSTITUTE_DOMAIN') ?? [];
        const parseFlex = (row: any) => {
            try {
                return JSON.parse(row.flexCol || '{}');
            } catch {
                return {};
            }
        };
        const isPublished = (row: any) => parseFlex(row).is_published == 1;

        if (urlInstituteId) {
            const urlRow = domainRows.find(
                (r: any) => String(r.code) === urlInstituteId
            );
            const groupId = urlRow
                ? String(parseFlex(urlRow).institute_group_id ?? '')
                : '';

            // Standalone institute — no group id resolved. Just show the URL
            // institute itself (if published). No dropdown will render.
            if (!groupId) {
                return urlRow && isPublished(urlRow)
                    ? [{ label: urlRow.value, value: String(urlRow.code) }]
                    : [];
            }

            // Every published institute sharing the same group_id, in
            // display_order. The URL institute itself is part of this set
            // when its flexCol.institute_group_id matches (which is the
            // common case — group root and children all reference the same
            // institute_group_id).
            return domainRows
                .filter(
                    (r: any) =>
                        String(parseFlex(r).institute_group_id ?? '') ===
                            groupId && isPublished(r)
                )
                .map((r: any) => ({
                    label: r.value,
                    value: String(r.code),
                }));
        }

        return sessionInstitutes.flatMap((code: any) => {
            const row: any = domainRows.find(
                (item: any) => item.code == code
            );
            if (!row || !isPublished(row)) return [];
            return [{ label: row.value, value: String(row.code) }];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedDomain, urlInstituteId, sessionState?.institutes]);

    // Group detection — only show the dropdown when there's something to
    // pick from. URL mode requires at least one child (= parent + child
    // makes 2 rows); management mode requires super-admin or multi-campus.
    const showCampusFilter = urlInstituteId
        ? campuses.length > 1
        : isSuperAdmin() || sessionInstitutes.length > 1;

    // Default selection. URL mode → just the URL id (admin clicked into
    // that specific institute, so that's what should render). Management
    // mode → every published campus ("All Institute" view).
    const [selectedCampuses, setSelectedCampuses] = useState<string[]>(() => {
        if (urlInstituteId) return [urlInstituteId];
        const domainRows: any[] =
            resolvedDomain?.get?.('INSTITUTE_DOMAIN') ?? [];
        const fromDomain = sessionInstitutes.flatMap((code: any) => {
            const row: any = domainRows.find(
                (item: any) => item.code == code
            );
            if (!row) return [];
            try {
                const flexCol = JSON.parse(row.flexCol || '{}');
                if (flexCol.is_published != 1) return [];
            } catch {
                return [];
            }
            return [String(row.code)];
        });
        return fromDomain.length
            ? fromDomain
            : sessionInstitutes.map((c: any) => String(c));
    });

    // If the domain hydrates after first render (rare), backfill the default.
    useEffect(() => {
        if (!urlInstituteId && campuses.length && selectedCampuses.length === 0) {
            setSelectedCampuses(campuses.map((c) => c.value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campuses]);

    // Final list of institute IDs that the dashboard fetch keys off.
    //   URL mode:
    //     - user toggling the dropdown → use the selection
    //     - dropdown empty (user unchecked everything) → fall back to the
    //       URL id alone (NOT the full child list — the admin opened that
    //       specific id from the institute list, so an empty selection
    //       should still render that id's data, not silently widen)
    //   Management mode:
    //     - user toggling → use the selection
    //     - dropdown empty → "All Institute" on the wire: fall back to the
    //       full campus list / sessionInstitutes / sessionState.instituteId
    //       so the dashboard isn't blank when nothing is checked
    const instituteIds: string[] = urlInstituteId
        ? selectedCampuses.length > 0
            ? selectedCampuses
            : [urlInstituteId]
        : selectedCampuses.length > 0
            ? selectedCampuses
            : campuses.length > 0
                ? campuses.map((c) => c.value)
                : sessionInstitutes.length > 0
                    ? sessionInstitutes.map((c: any) => String(c))
                    : sessionState?.instituteId
                        ? [String(sessionState.instituteId)]
                        : [];

    // First id drives FE-only INSTITUTE_DOMAIN flexCol lookups
    // (commitment_paths, institute_type tag).
    const instituteId = instituteIds[0] || '';

    /* ---------------- SMART CONTEXT ---------------- */

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(
        smartReducer,
        {
            flags: { isDataLoading: true, showFormErrors: 0 },
            actions: {},
        } as any
    );

    useEffect(() => {
        const baseDomain =
            loadedDomain ?? new Map([...(globalState.domain || new Map())]);
        const domain = addUserSpecificDomains(new Map([...baseDomain]), data ?? {});

        dispatch({
            type: 'DATA_INIT',
            payload: {
                formConfig: formConfig ?? {},
                data: data ?? {},
                domain,
                routeInfo: routeInfo ?? { instituteId },
            },
        });
    }, []);

    /* ---------------- INSTITUTE FLEXCOL → COMMITMENT PATHS + TAG ----------------
       In multi-campus mode the dashboard aggregates across all selected
       campuses, so the goal tabs / level cards / signal rows should reflect
       the *union* of every selected campus's commitment_paths — otherwise
       the page silently shows "no data" whenever the first id happens to be
       a parent / non-published institute that has no commitment_paths set.
    */

    const enabledCommitmentPaths: string[] = useMemo(() => {
        if (!instituteIds.length) return [];
        const all = new Set<string>();
        instituteIds.forEach((id) => {
            const fc = getFlexColDomainValueForCode(
                String(id),
                'INSTITUTE_DOMAIN',
                state
            );
            if (isEmpty(fc)) return;
            try {
                const parsed = JSON.parse(fc);
                (parsed?.commitment_paths || []).forEach((p: string) =>
                    all.add(p)
                );
            } catch {
                /* malformed flexCol — skip */
            }
        });
        return Array.from(all);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instituteIds.join(','), state.domain]);

    // Institute tag — pick the first selected campus's type. In a mixed-type
    // group (unlikely but possible) we'd default to 'college' which is the
    // safer fall-through for signal visibility.
    const instituteTag: 'college' | 'k12' = useMemo(() => {
        const fc = getFlexColDomainValueForCode(
            String(instituteId),
            'INSTITUTE_DOMAIN',
            state
        );
        if (isEmpty(fc)) return 'college';
        try {
            const t = String(JSON.parse(fc)?.institute_type || '').toUpperCase();
            if (t === 'SCHOOL' || t === 'K12_SCHOOL' || t === 'PRE_SCHOOL')
                return 'k12';
        } catch {
            /* fall through */
        }
        return 'college';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instituteId, state.domain]);

    /* ---------------- BUILD GOALS / LEVELS / SIGNALS FROM DOMAIN ---------------- */

    const goals = useMemo(() => {
        const pathwayGoals = state.domain?.get('PATHWAY_GOAL') ?? [];
        const pathwayLevels = state.domain?.get('PATHWAY_LEVEL') ?? [];
        const pathwaySignals = state.domain?.get('PATHWAY_SIGNAL') ?? [];

        return pathwayGoals
            .filter((g: any) => enabledCommitmentPaths.includes(g.code))
            .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
            .map((g: any) => {
                const goalMeta = !isEmpty(g.flexCol) ? JSON.parse(g.flexCol) : {};
                const levels = pathwayLevels
                    .filter((l: any) => l.parentCode === g.code)
                    .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
                    .map((l: any, idx: number) => {
                        const levelMeta = !isEmpty(l.flexCol)
                            ? JSON.parse(l.flexCol)
                            : {};
                        const signals = pathwaySignals
                            .filter((s: any) => s.parentCode === l.code)
                            .map((s: any) => {
                                const sMeta = !isEmpty(s.flexCol)
                                    ? JSON.parse(s.flexCol)
                                    : {};
                                return {
                                    code: s.code,
                                    label: s.value,
                                    institute_tag: sMeta.institute_tag || 'all',
                                    level_number:
                                        sMeta.level_number || levelMeta.level_number,
                                };
                            })
                            .filter(
                                (s: any) =>
                                    s.institute_tag === 'all' ||
                                    s.institute_tag === instituteTag
                            )
                            .sort(
                                (a: any, b: any) => a.displayOrder - b.displayOrder
                            );
                        return {
                            number: levelMeta.level_number || idx + 1,
                            code: l.code,
                            name: l.value,
                            signals,
                        };
                    })
                    .filter((l: any) => l.signals.length > 0);
                return {
                    code: g.code,
                    name: g.value,
                    theme: {
                        color_primary: goalMeta.color_primary || '#166534',
                        color_mid: goalMeta.color_mid || '#4caf50',
                        color_light: goalMeta.color_light || '#e8f5e9',
                    },
                    levels,
                };
            })
            .filter((g: any) => g.levels.length > 0);
    }, [state.domain, enabledCommitmentPaths, instituteTag]);

    /* ---------------- ACTIVE GOAL + DASHBOARD ---------------- */

    const [activeGoal, setActiveGoal] = useState('');
    const [dashboard, setDashboard] = useState<any>(null);
    const [search, setSearch] = useState('');
    const [levelFilter, setLevelFilter] = useState<number | null>(null);

    // Campus dropdown open/close + outside-click — same UX as
    // DashboardAccordianItem on /management/dashboard.
    const [campusDropdownOpen, setCampusDropdownOpen] = useState(false);
    const campusDropdownRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!showCampusFilter) return;
        const handleOutsideClick = (e: MouseEvent) => {
            if (
                campusDropdownRef.current &&
                !campusDropdownRef.current.contains(e.target as Node)
            ) {
                setCampusDropdownOpen(false);
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [showCampusFilter]);

    const allCampusesSelected =
        campuses.length > 0 && selectedCampuses.length === campuses.length;
    const toggleAllCampuses = () => {
        setSelectedCampuses(
            allCampusesSelected ? [] : campuses.map((c) => c.value)
        );
    };
    const toggleCampus = (value: string) => {
        setSelectedCampuses((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };
    const campusButtonLabel = allCampusesSelected
        ? 'All Institute'
        : selectedCampuses.length === 0
            ? 'Select Campus'
            : selectedCampuses
                  .map(
                      (v) => campuses.find((c) => c.value === v)?.label || v
                  )
                  .join(', ');

    useEffect(() => {
        if (goals.length && !activeGoal) setActiveGoal(goals[0].code);
    }, [goals]);

    // `submittedSearch` is what gets posted to the backend. It only updates
    // when the agent presses Enter or clicks the search icon — the input is
    // never debounced. Empty/cleared search submits as no filter.
    const [submittedSearch, setSubmittedSearch] = useState('');
    const onSubmitSearch = () => setSubmittedSearch(search.trim());
    const onClearSearch = () => {
        setSearch('');
        setSubmittedSearch('');
    };

    // Joined string in the dep array so the effect re-fires on campus
    // selection changes (a new array reference alone wouldn't be stable).
    const idsKey = instituteIds.join(',');
    useEffect(() => {
        if (!idsKey || !activeGoal) return;
        // Only the submitted search is server-side. Level filter is client-side
        // so the level cards still show full-cohort counts when a level is selected.
        CommitmentService.getDashboard(instituteIds, activeGoal, {
            search: submittedSearch || null,
        }).then(setDashboard);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey, activeGoal, submittedSearch]);

    // When the institute has no commitment_paths configured, fall back to a
    // neutral placeholder goal so the dashboard shell still renders with all
    // its charts, cards and tables in a zero-data state (rather than blocking
    // the page with an error-like message).
    const placeholderGoal = {
        code: '',
        name: 'Commitment',
        theme: {
            color_primary: '#94a3b8',
            color_mid: '#cbd5e1',
            color_light: '#f1f5f9',
        },
        levels: [] as any[],
    };
    const currentGoal =
        goals.find((g: any) => g.code === activeGoal) || goals[0] || placeholderGoal;
    const hasGoals = goals.length > 0;

    /* ---------------- DERIVED METRICS ---------------- */

    const dashSignals: any[] = dashboard?.signals ?? [];
    const dashAlumni: any[] = dashboard?.alumni ?? [];
    const dashUserSignals: any[] = dashboard?.userSignals ?? [];

    // Map<userId, Map<signalCode, {willing, doneCount}>> — drives the
    // 13×13 signal-square colors on the alumni engagement list.
    const userSignalsByUser = useMemo(() => {
        const m: Record<string, Record<string, any>> = {};
        dashUserSignals.forEach((r: any) => {
            if (!m[r.userId]) m[r.userId] = {};
            m[r.userId][r.signalCode] = {
                willing: r.willing,
                doneCount: Number(r.doneCount) || 0,
            };
        });
        return m;
    }, [dashUserSignals]);

    const levelBuckets = useMemo(() => {
        const b: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
        dashAlumni.forEach((a) => {
            const lv = Number(a.assignedLevel) || 0;
            b[lv] = (b[lv] || 0) + 1;
        });
        return b;
    }, [dashAlumni]);

    const engaged =
        (levelBuckets[1] || 0) + (levelBuckets[2] || 0) + (levelBuckets[3] || 0);
    const unengaged = levelBuckets[0] || 0;

    // Build signal rows joined with taxonomy, grouped by level for rowspan render
    const signalRowsByLevel = useMemo(() => {
        if (!currentGoal) return [] as Array<any>;
        const map: Record<string, any> = {};
        currentGoal.levels.forEach((l: any) =>
            l.signals.forEach((s: any) => {
                map[s.code] = { ...s, levelName: l.name, levelNumber: l.number };
            })
        );

        const byLevel: Record<number, any[]> = {};
        dashSignals.forEach((r) => {
            const m = map[r.signalCode];
            if (!m) return;
            const arr = byLevel[m.levelNumber] || (byLevel[m.levelNumber] = []);
            arr.push({
                signalCode: r.signalCode,
                label: m.label,
                institute_tag: m.institute_tag,
                levelName: m.levelName,
                levelNumber: m.levelNumber,
                willing: Number(r.willing) || 0,
                done: Number(r.done) || 0,
                totalDone: Number(r.totalDone) || 0,
                // Spec: Total = sum of all done_count values across alumni
                // (not unique alumni count). totalDone from backend is SUM(done_count).
                total: Number(r.totalDone) || 0,
            });
        });
        return Object.keys(byLevel)
            .map(Number)
            .sort((a, b) => a - b)
            .map((lv) => ({
                levelNumber: lv,
                levelName:
                    currentGoal.levels.find((l: any) => l.number === lv)?.name ||
                    `L${lv}`,
                rows: byLevel[lv],
            }));
    }, [dashSignals, currentGoal]);

    // Flat list of signals on the active goal (for the alumni list "Signals" column squares).
    const goalSignalFlat = useMemo(() => {
        if (!currentGoal) return [] as Array<any>;
        const out: any[] = [];
        currentGoal.levels.forEach((l: any) =>
            l.signals.forEach((s: any) => out.push({ ...s, levelNumber: l.number }))
        );
        return out;
    }, [currentGoal]);

    const levelSummary = useMemo(() => {
        if (!currentGoal) return '';
        const parts = currentGoal.levels.map(
            (l: any) => `L${l.number}: ${l.name}`
        );
        parts.push(instituteTag === 'k12' ? 'K-12 mode' : 'College mode');
        return parts.join(' | ');
    }, [currentGoal, instituteTag]);

    /* ---------------- CHART.JS WIRING ---------------- */

    const scatterRef = useRef<HTMLCanvasElement | null>(null);
    const doughnutRef = useRef<HTMLCanvasElement | null>(null);
    const pipelineRef = useRef<HTMLCanvasElement | null>(null);
    const chartInstancesRef = useRef<any[]>([]);

    useEffect(() => {
        chartInstancesRef.current.forEach((c) => c.destroy());
        chartInstancesRef.current = [];
        if (!currentGoal) return;

        const primary = currentGoal.theme?.color_primary || '#166534';
        const mid = currentGoal.theme?.color_mid || '#4caf50';
        const light = currentGoal.theme?.color_light || '#e8f5e9';

        // Convert "#RRGGBB" → "rgba(r,g,b,a)" so we can apply per-level opacity
        // on the scattergram inner fill while keeping the outer ring at the
        // goal's solid primary.
        const hexToRgba = (hex: string, a: number) => {
            const h = (hex || '').replace('#', '');
            const r = parseInt(h.substring(0, 2) || '00', 16);
            const g = parseInt(h.substring(2, 4) || '00', 16);
            const b = parseInt(h.substring(4, 6) || '00', 16);
            return `rgba(${r},${g},${b},${a})`;
        };

        // Scattergram — lane-style: 4 vertical bands (Unengaged / L1 / L2 / L3)
        // each with a header bar (level name from PATHWAY_LEVEL.value) and a
        // watermark count at the bottom. Dots are jittered horizontally so
        // alumni sharing the same level don't stack into a single column.
        if (scatterRef.current) {
            // Stable per-alumnus jitter so dot positions don't shuffle on
            // re-render. Output range -0.3..+0.3 inside the ±0.5 lane width.
            const stableJitter = (id: string) => {
                let h = 0;
                for (let i = 0; i < id.length; i++) {
                    h = ((h << 5) - h + id.charCodeAt(i)) | 0;
                }
                return (((h % 1000) + 1000) % 1000) / 1000 * 0.6 - 0.3;
            };

            const pts = dashAlumni
                .filter((a) => Number(a.assignedLevel) > 0)
                .map((a) => ({
                    x: Number(a.assignedLevel) + stableJitter(String(a.userId || '')),
                    y:
                        Number(a.maxScore) > 0
                            ? Number(a.score) / Number(a.maxScore)
                            : 0,
                    lv: Number(a.assignedLevel),
                }));

            // Lane decorations: header bars (level name) + watermark counts.
            // Drawn beforeDatasetsDraw so dots paint on top. Headers live in
            // the canvas's top padding (layout.padding.top) — outside the
            // chartArea — so they never collide with markers at y=100%.
            const lanePlugin = {
                id: 'commitmentLanes',
                beforeDatasetsDraw(chart: any) {
                    const {
                        ctx,
                        chartArea,
                        scales: { x: xScale },
                    } = chart;
                    const { top, bottom } = chartArea;
                    const headerH = 28;
                    const headerTop = top - headerH - 2;

                    const lanes = [0, 1, 2, 3].map((lv) => {
                        const lvObj = currentGoal?.levels?.find(
                            (l: any) => Number(l.number) === lv
                        );
                        return {
                            lv,
                            name:
                                lv === 0
                                    ? 'Unengaged'
                                    : lvObj?.name
                                    ? `L${lv}: ${lvObj.name}`
                                    : `L${lv}`,
                            count: levelBuckets[lv] || 0,
                        };
                    });

                    lanes.forEach((lane) => {
                        const leftPx = xScale.getPixelForValue(lane.lv - 0.5);
                        const rightPx = xScale.getPixelForValue(lane.lv + 0.5);
                        const w = rightPx - leftPx;
                        const cx = (leftPx + rightPx) / 2;
                        const isUnengaged = lane.lv === 0;

                        ctx.save();

                        // Lane header bar (above the plot area)
                        ctx.fillStyle = isUnengaged ? '#f1f5f9' : '#ecfdf5';
                        ctx.fillRect(leftPx + 2, headerTop, w - 4, headerH);
                        ctx.font =
                            '600 11px system-ui, -apple-system, "Segoe UI", sans-serif';
                        ctx.fillStyle = isUnengaged ? '#94a3b8' : '#475569';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(lane.name, cx, headerTop + headerH / 2);

                        // Subtle lane separator
                        if (lane.lv < 3) {
                            ctx.strokeStyle = '#eef0f2';
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(rightPx, top);
                            ctx.lineTo(rightPx, bottom);
                            ctx.stroke();
                        }

                        // Watermark count (faded, bottom of lane)
                        ctx.font =
                            '700 22px system-ui, -apple-system, "Segoe UI", sans-serif';
                        ctx.fillStyle = 'rgba(148, 163, 184, 0.32)';
                        const countText =
                            isUnengaged && lane.count === 0
                                ? '—'
                                : String(lane.count);
                        ctx.fillText(countText, cx, bottom - 18);

                        ctx.restore();
                    });
                },
            };

            const c = new Chart(scatterRef.current, {
                type: 'scatter',
                data: {
                    datasets: [
                        {
                            label: 'Alumni',
                            data: pts as any,
                            clip: false,
                            backgroundColor: (ctx: any) => {
                                const lv = ctx.raw?.lv || 1;
                                const opacity = Math.min(1, 0.4 + 0.2 * lv);
                                return hexToRgba(primary, opacity);
                            },
                            borderColor: primary,
                            borderWidth: 1.5,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    // Reserve top padding for the lane-header bars drawn by
                    // the plugin (headerH + buffer).
                    layout: { padding: { top: 36, right: 10 } },
                    plugins: {
                        legend: { display: false },
                        // Tooltip — X shows the level bucket (L1/L2/L3, or
                        // Unengaged), Y shows engagement intensity as a
                        // percentage. Avoids leaking the jittered raw x and
                        // the unrounded 0..1 y to the user.
                        tooltip: {
                            callbacks: {
                                title: (items: any) => {
                                    const lv = Number(items?.[0]?.raw?.lv) || 0;
                                    return lv === 0 ? 'Unengaged' : `L${lv}`;
                                },
                                label: (item: any) => {
                                    const y = Number(item?.raw?.y) || 0;
                                    return `${Math.round(y * 100)}%`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            min: -0.5,
                            max: 3.5,
                            // Force ticks at lane centers regardless of how
                            // Chart.js's auto-stepping would land them
                            // (stepSize alone starts from `min` and steps by 1,
                            // producing -0.5 / 0.5 / 1.5 / 2.5 / 3.5 which the
                            // callback can't label).
                            afterBuildTicks: (axis: any) => {
                                axis.ticks = [0, 1, 2, 3].map((v) => ({ value: v }));
                            },
                            ticks: {
                                callback: (v: any) => {
                                    const n = Number(v);
                                    if (n === 0) return '—';
                                    return `Level ${n}`;
                                },
                            },
                            grid: { display: false },
                            title: { display: false },
                        },
                        y: {
                            min: 0,
                            max: 1,
                            ticks: {
                                stepSize: 0.25,
                                callback: (v) => `${Math.round(Number(v) * 100)}%`,
                            },
                            grid: { color: '#f1f5f9' },
                            title: {
                                display: true,
                                text: 'Engagement Intensity →',
                                font: { size: 11 },
                                color: '#94a3b8',
                            },
                        },
                    },
                },
                plugins: [lanePlugin],
            });
            chartInstancesRef.current.push(c);
        }

        // Level distribution doughnut
        if (doughnutRef.current) {
            const c = new Chart(doughnutRef.current, {
                type: 'doughnut',
                data: {
                    labels: ['L1', 'L2', 'L3', 'Unengaged'],
                    datasets: [
                        {
                            data: [
                                levelBuckets[1] || 0,
                                levelBuckets[2] || 0,
                                levelBuckets[3] || 0,
                                levelBuckets[0] || 0,
                            ],
                            backgroundColor: [light, mid, primary, '#e2e8f0'],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '60%',
                    plugins: { legend: { position: 'bottom' } },
                },
            });
            chartInstancesRef.current.push(c);
        }

        // Signal Pipeline — horizontal GROUPED bar chart (Already Done vs Willing).
        // Two bars per signal so the conversion gap (willing → done) is visible.
        if (pipelineRef.current) {
            const truncate = (s: string) =>
                s.length > 36 ? s.slice(0, 35) + '…' : s;
            const labels = signalRowsByLevel.flatMap((g) =>
                g.rows.map((r: any) => truncate(r.label))
            );
            const willing = signalRowsByLevel.flatMap((g) =>
                g.rows.map((r: any) => r.willing)
            );
            const done = signalRowsByLevel.flatMap((g) =>
                g.rows.map((r: any) => r.done)
            );
            const c = new Chart(pipelineRef.current, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Already Done',
                            data: done,
                            backgroundColor: primary,
                            borderRadius: 3,
                            barPercentage: 0.65,
                            categoryPercentage: 0.65,
                        },
                        {
                            label: 'Willing',
                            data: willing,
                            backgroundColor: mid,
                            borderRadius: 3,
                            barPercentage: 0.65,
                            categoryPercentage: 0.65,
                        },
                    ],
                },
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'top',
                            align: 'start',
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'rect',
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 14,
                                font: { size: 12 },
                            },
                        },
                    },
                    scales: {
                        x: {
                            beginAtZero: true,
                            ticks: { precision: 0 },
                            title: { display: true, text: 'Alumni count' },
                        },
                        y: {
                            ticks: { font: { size: 11 } },
                        },
                    },
                },
            });
            chartInstancesRef.current.push(c);
        }

        return () => {
            chartInstancesRef.current.forEach((c) => c.destroy());
            chartInstancesRef.current = [];
        };
    }, [currentGoal, dashAlumni, levelBuckets, signalRowsByLevel]);

    /* ---------------- ALUMNI ROWS ---------------- */

    // Client-side level filter (server returns the full goal cohort so the
    // level cards stay accurate). Default = all engaged (assignedLevel > 0).
    const filteredAlumni = useMemo(() => {
        const base = [...dashAlumni].sort(
            (a, b) => Number(b.assignedLevel) - Number(a.assignedLevel)
        );
        if (levelFilter === null) {
            return base.filter((a) => Number(a.assignedLevel) > 0);
        }
        return base.filter((a) => Number(a.assignedLevel) === levelFilter);
    }, [dashAlumni, levelFilter]);

    // Dynamic header — "Alumni — Level N: {name}" when filtered, else "All Engaged"
    const alumniListTitle = useMemo(() => {
        if (levelFilter === null) return 'Alumni — All Engaged';
        if (levelFilter === 0) return 'Alumni — Unengaged';
        const lvl = currentGoal?.levels?.find((l: any) => l.number === levelFilter);
        return `Alumni — Level ${levelFilter}${lvl ? `: ${lvl.name}` : ''}`;
    }, [levelFilter, currentGoal]);

    /* ---------------- UI ---------------- */

    if (state.flags?.isDataLoading) return <div className="p-4">Loading...</div>;

    // Active goal's theme — every chart, badge, bar, and square in the goal
    // panel resolves through these so the dashboard repaints when the user
    // switches to e.g. Career Guidance (dark blue) or Fundraise (orange).
    const primary = currentGoal.theme?.color_primary || '#166534';
    const mid = currentGoal.theme?.color_mid || '#4caf50';
    const light = currentGoal.theme?.color_light || '#e8f5e9';

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="container-fluid py-3" style={{ maxWidth: 1140 }}>

                {/* =================== CAMPUS FILTER ===================
                    Shown only when:
                      - no :instituteId in URL (i.e. management nav, not admin
                        /commitment/tracker/:instituteId), AND
                      - user is super-admin OR institute is a group institute
                        (sessionState.institutes has > 1 entry)
                    Multi-select checkbox dropdown mirrors the UX on
                    /management/dashboard (DashboardAccordianItem). */}
                {showCampusFilter && (
                    <div
                        className="d-flex flex-wrap align-items-center gap-3 p-3 mb-3 rounded-3"
                        style={{
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                        }}
                    >
                        <label
                            className="fw-semibold mb-0"
                            style={{ fontSize: 13, color: '#475569' }}
                        >
                            Campus
                        </label>
                        <div
                            className={`dropdown ${campusDropdownOpen ? 'open' : ''}`}
                            ref={campusDropdownRef}
                            style={{ minWidth: 280, maxWidth: 420 }}
                        >
                            <button
                                type="button"
                                className="filter-custom-select dashboard-filter w-100 text-start"
                                onClick={() =>
                                    setCampusDropdownOpen(!campusDropdownOpen)
                                }
                                title={campusButtonLabel}
                                style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',                                   
                                }}
                            >
                                {campusButtonLabel}
                            </button>
                            <div
                                className="dropdown-menu"
                                style={{
                                    display: campusDropdownOpen ? 'block' : 'none',
                                    background: 'white',
                                    maxHeight: 280,
                                    overflowY: 'auto',
                                    minWidth: 280,
                                     zIndex: 9,
                                }}
                            >
                                <div className="dropdown-wrap">
                                    <div className="dropdown-item">
                                        <input
                                            type="checkbox"
                                            id="commitment-allCampus"
                                            className="form-check-input me-1"
                                            checked={allCampusesSelected}
                                            onChange={toggleAllCampuses}
                                        />
                                        <label
                                            htmlFor="commitment-allCampus"
                                            className="form-check-label"
                                        >
                                            All Institute
                                        </label>
                                    </div>
                                    {campuses.map((c) => (
                                        <div
                                            key={c.value}
                                            className="dropdown-item"
                                        >
                                            <input
                                                type="checkbox"
                                                id={`commitment-campus-${c.value}`}
                                                className="form-check-input me-1"
                                                checked={selectedCampuses.includes(
                                                    c.value
                                                )}
                                                onChange={() =>
                                                    toggleCampus(c.value)
                                                }
                                            />
                                            <label
                                                htmlFor={`commitment-campus-${c.value}`}
                                                className="form-check-label"
                                            >
                                                {c.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* =================== HEADER CARD =================== */}
                <div
                    className="rounded-4 p-4 p-lg-5 mb-4 shadow-sm overflow-hidden position-relative"
                    style={{
                        background:
                            'linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#166534 100%)',
                        color: '#fff',
                    }}
                >
                    <div className="row align-items-end g-4 position-relative">
                        <div className="col-lg-8">
                            <div
                                className="text-uppercase fw-semibold mb-2"
                                style={{
                                    letterSpacing: '.12em',
                                    fontSize: 12,
                                    color: 'rgba(255,255,255,.7)',
                                }}
                            >
                                Track
                            </div>
                            <h1
                                className="mb-2 fw-bold"
                                style={{ fontSize: 'clamp(28px,4vw,42px)' }}
                            >
                                Alumni Commitment Dashboard
                            </h1>
                            <p
                                className="mb-3"
                                style={{
                                    maxWidth: 760,
                                    color: 'rgba(255,255,255,.8)',
                                    fontSize: 15,
                                }}
                            >
                                Track engagement milestones and giving trends in real-time to
                                strengthen your lifelong connection with our graduates.
                            </p>
                            {/* Institute heading. Three render modes:
                                  - admin URL or single-institute session →
                                    single name + tag suffix
                                  - multi-campus, all selected →
                                    "All Institute (campus1, campus2, …)"
                                  - multi-campus, partial selection →
                                    comma-joined names of selected campuses */}
                            <p
                                className="mb-3"
                                style={{
                                    color: 'rgba(255,255,255,.75)',
                                    fontSize: 14,
                                }}
                            >
                                Institute:{' '}
                                <span className="fw-medium text-white text-opacity-75">
                                    {(() => {
                                        // Single-institute display (no dropdown
                                        // or admin URL) — keep the original
                                        // "name (type)" formatting.
                                        if (!showCampusFilter) {
                                            const nm = getDomainValueForCode(
                                                String(instituteId),
                                                'INSTITUTE_DOMAIN',
                                                state
                                            );
                                            const typeLabel =
                                                instituteTag === 'k12'
                                                    ? 'K-12 School'
                                                    : 'College / University';
                                            return `${nm} (${typeLabel})`;
                                        }

                                        // Multi-campus mode — resolve labels
                                        // for every selected campus.
                                        const labels = selectedCampuses
                                            .map(
                                                (v) =>
                                                    campuses.find(
                                                        (c) => c.value === v
                                                    )?.label ||
                                                    getDomainValueForCode(
                                                        String(v),
                                                        'INSTITUTE_DOMAIN',
                                                        state
                                                    ) ||
                                                    v
                                            )
                                            .filter(Boolean);

                                        if (labels.length === 0) {
                                            return 'No campuses selected';
                                        }
                                        if (allCampusesSelected) {
                                            return `All Institute (${labels.join(', ')})`;
                                        }
                                        return labels.join(', ');
                                    })()}
                                </span>
                            </p>
                            <div className="w-100" style={{ maxWidth: 420 }}>
                                <label
                                    htmlFor="alumni-dashboard-search"
                                    className="form-label mb-2 fw-semibold"
                                    style={{
                                        fontSize: 13,
                                        color: 'rgba(255,255,255,.84)',
                                    }}
                                >
                                    Search alumni
                                </label>
                                <div className="input-group">
                                    <input
                                        type="search"
                                        className="form-control border-0"
                                        id="alumni-dashboard-search"
                                        placeholder="Search by name, batch, or program"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                onSubmitSearch();
                                            }
                                        }}
                                    />
                                    {submittedSearch && (
                                        <button
                                            type="button"
                                            className="btn btn-light border-0"
                                            onClick={onClearSearch}
                                            title="Clear search"
                                            aria-label="Clear search"
                                        >
                                            <i className="bi bi-x-lg text-secondary" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-light border-0"
                                        onClick={onSubmitSearch}
                                        title="Search"
                                        aria-label="Search alumni"
                                    >
                                        <i className="bi bi-search text-secondary" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* Read-only institute_type toggle chips
                        <div className="col-lg-4">
                            <div
                                className="d-flex flex-wrap gap-2 justify-content-lg-end"
                                id="inst-toggle"
                            >
                                <button
                                    type="button"
                                    className="btn btn-sm rounded-pill px-3 py-2 fw-semibold"
                                    data-inst="college"
                                    disabled
                                    style={
                                        instituteTag === 'college'
                                            ? {
                                                  fontSize: 13,
                                                  background: '#166534',
                                                  borderColor: '#166534',
                                                  color: '#fff',
                                                  boxShadow:
                                                      '0 10px 24px rgba(22,101,52,.18)',
                                              }
                                            : {
                                                  fontSize: 13,
                                                  background: '#fff',
                                                  borderColor: '#d1d5db',
                                                  color: '#475569',
                                              }
                                    }
                                >
                                    College / University
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm rounded-pill px-3 py-2 fw-semibold"
                                    data-inst="k12"
                                    disabled
                                    style={
                                        instituteTag === 'k12'
                                            ? {
                                                  fontSize: 13,
                                                  background: '#166534',
                                                  borderColor: '#166534',
                                                  color: '#fff',
                                                  boxShadow:
                                                      '0 10px 24px rgba(22,101,52,.18)',
                                              }
                                            : {
                                                  fontSize: 13,
                                                  background: '#fff',
                                                  borderColor: '#d1d5db',
                                                  color: '#475569',
                                              }
                                    }
                                >
                                    K-12 School
                                </button>
                            </div>
                        </div>
                        */}
                    </div>
                </div>

                {/* =================== ALUMNI DASHBOARD ROOT =================== */}
                <div id="alumni-dashboard-root">
                    {/* GOAL TAB STRIP — rectangle buttons with shadow */}
                    <div className="d-flex flex-wrap gap-1 mb-4">
                        {!hasGoals && (
                            <button
                                type="button"
                                disabled
                                className="btn rounded px-3 py-2 fw-medium"
                                style={{
                                    fontSize: 13,
                                    background: '#fff',
                                    color: '#94a3b8',
                                    borderColor: '#e5e7eb',
                                    cursor: 'default',
                                }}
                            >
                                — No goals configured —
                            </button>
                        )}
                        {goals.map((g: any) => {
                            const isActive = g.code === activeGoal;
                            // Each tab paints with its own theme so the strip
                            // visually previews the per-goal color scheme.
                            const goalPrimary =
                                g.theme?.color_primary || '#166534';
                            return (
                                <button
                                    key={g.code}
                                    type="button"
                                    onClick={() => {
                                        setActiveGoal(g.code);
                                        setLevelFilter(null);
                                    }}
                                    className="btn rounded px-3 py-2 fw-medium"
                                    data-goal={g.name}
                                    style={
                                        isActive
                                            ? {
                                                  fontSize: 13,
                                                  background: goalPrimary,
                                                  color: '#fff',
                                                  borderColor: goalPrimary,
                                                  boxShadow:
                                                      '0 14px 26px rgba(15,23,42,.12)',
                                              }
                                            : {
                                                  fontSize: 13,
                                                  background: '#fff',
                                                  color: '#334155',
                                                  borderColor: '#e5e7eb',
                                              }
                                    }
                                >
                                    {g.name}
                                </button>
                            );
                        })}
                    </div>

                    {/* DASHBOARD FOCUS */}
                    <div className="bg-white border rounded-4 shadow-sm p-4 mb-4">
                        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-end">
                            <div>
                                <div
                                    className="text-uppercase fw-semibold text-secondary mb-2"
                                    style={{ letterSpacing: '.12em', fontSize: 11 }}
                                >
                                    Dashboard Focus
                                </div>
                                <h2 className="mb-1 fw-bold" style={{ color: '#0f3d23' }}>
                                    {currentGoal.name}
                                </h2>
                                <div className="text-secondary">{levelSummary}</div>
                            </div>
                            <div className="d-flex gap-4 flex-wrap">
                                <div>
                                    <div className="text-secondary small">Engaged Alumni</div>
                                    <div
                                        className="display-6 fw-bold lh-1"
                                        style={{ color: primary }}
                                    >
                                        {engaged}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-secondary small">Unengaged</div>
                                    <div
                                        className="display-6 fw-bold lh-1"
                                        style={{ color: '#94a3b8' }}
                                    >
                                        {unengaged}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* LEVEL CARDS — 4 in a row */}
                    <div className="row g-3 mb-4">
                        {currentGoal.levels.map((l: any) => {
                            const count = levelBuckets[l.number] || 0;
                            const active = levelFilter === l.number;
                            return (
                                <div key={l.code} className="col-12 col-md-6 col-xl">
                                    <div
                                        onClick={() =>
                                            setLevelFilter(active ? null : l.number)
                                        }
                                        className="border rounded-4 p-4 h-100"
                                        data-level={l.number}
                                        style={{
                                            cursor: 'pointer',
                                            minHeight: 140,
                                            background: active ? light : '#fff',
                                            borderColor: active ? primary : '#e5e7eb',
                                            color: '#111827',
                                        }}
                                    >
                                        <div className="d-flex justify-content-between gap-3">
                                            <div>
                                                <span
                                                    className="badge rounded-pill px-3 py-2 fw-bold"
                                                    style={{
                                                        fontSize: 11,
                                                        letterSpacing: '.03em',
                                                        background: light,
                                                        color: primary,
                                                    }}
                                                >
                                                    Level {l.number}
                                                </span>
                                                <div
                                                    className="fw-bold mt-2"
                                                    style={{ fontSize: 18, color: '#0f172a' }}
                                                >
                                                    {l.name}
                                                </div>
                                            </div>
                                            <div
                                                className="fw-bold lh-1"
                                                style={{ fontSize: 32, color: primary }}
                                            >
                                                {count}
                                            </div>
                                        </div>
                                        <div
                                            className="mt-3 small"
                                            style={{ color: '#64748b' }}
                                        >
                                            {l.signals.length} signals
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Unengaged (Open) card */}
                        <div className="col-12 col-md-6 col-xl">
                            <div
                                onClick={() =>
                                    setLevelFilter(levelFilter === 0 ? null : 0)
                                }
                                className="border rounded-4 p-4 h-100"
                                style={{
                                    background: levelFilter === 0 ? light : '#f8fafc',
                                    borderStyle: 'dashed',
                                    borderColor: levelFilter === 0 ? primary : '#d1d5db',
                                    minHeight: 140,
                                    cursor: 'pointer',
                                }}
                            >
                                <div className="d-flex justify-content-between gap-3">
                                    <div>
                                        <span
                                            className="badge rounded-pill px-3 py-2 fw-bold"
                                            style={{
                                                fontSize: 11,
                                                letterSpacing: '.03em',
                                                background: '#e2e8f0',
                                                color: '#64748b',
                                            }}
                                        >
                                            Open
                                        </span>
                                        <div className="fw-bold mt-2" style={{ fontSize: 18 }}>
                                            Unengaged
                                        </div>
                                    </div>
                                    <div
                                        className="fw-bold lh-1"
                                        style={{ fontSize: 32, color: '#94a3b8' }}
                                    >
                                        {unengaged}
                                    </div>
                                </div>
                                <div className="mt-3 small text-secondary">
                                    Opportunity pool
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SCATTERGRAM + (DOUGHNUT + FUNNEL) */}
                    <div className="row g-4 mb-4">
                        <div className="col-12 col-lg-7">
                            <div className="bg-white border rounded-4 shadow-sm p-4 h-100">
                                <h3 className="fw-bold mb-1" style={{ fontSize: 18 }}>
                                    Alumni Commitment Scattergram
                                </h3>
                                <p
                                    className="text-secondary mb-3"
                                    style={{ fontSize: 13 }}
                                >
                                    Each dot = 1 alumni positioned by assigned level (X) and
                                    engagement intensity (Y). Dots cluster by commitment tier;
                                    larger / darker = deeper engagement.
                                </p>
                                <div
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        overflow: 'hidden',
                                        borderRadius: 10,
                                        background: '#fafbfc',
                                        border: '1px solid #eef0f2',
                                        height: 300,
                                    }}
                                >
                                    <canvas ref={scatterRef} />
                                    {dashAlumni.filter((a) => Number(a.assignedLevel) > 0)
                                        .length === 0 && (
                                        <div
                                            className="position-absolute top-50 start-50 translate-middle text-secondary"
                                            style={{ fontSize: 13 }}
                                        >
                                            No Data Found
                                        </div>
                                    )}
                                </div>
                                <div
                                    className="d-flex gap-3 mt-2 flex-wrap"
                                    id="sc-legend"
                                    style={{ fontSize: 10.5, color: '#9ca3af' }}
                                >
                                    {currentGoal.levels.map((l: any) => (
                                        <span key={l.code}>
                                            <span
                                                className="d-inline-block rounded-circle align-middle me-1"
                                                style={{
                                                    width: 10,
                                                    height: 10,
                                                    background: primary,
                                                    opacity: Math.min(
                                                        1,
                                                        0.4 + 0.2 * l.number - 0.07
                                                    ),
                                                }}
                                            />
                                            L{l.number}: {l.name}
                                        </span>
                                    ))}
                                    <span>
                                        <span
                                            className="d-inline-block rounded-circle align-middle me-1"
                                            style={{
                                                width: 10,
                                                height: 10,
                                                background: '#dde1e6',
                                            }}
                                        />
                                        Unengaged
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-5">
                            <div className="d-flex flex-column gap-4 h-100">
                                {/* Level Distribution */}
                                <div className="bg-white border rounded-4 shadow-sm p-4">
                                    <h3 className="fw-bold mb-3" style={{ fontSize: 18 }}>
                                        Level Distribution
                                    </h3>
                                    <div
                                        style={{ position: 'relative', height: 220 }}
                                    >
                                        <canvas ref={doughnutRef} />
                                        {engaged + unengaged === 0 && (
                                            <div
                                                className="position-absolute top-50 start-50 translate-middle text-secondary"
                                                style={{ fontSize: 13 }}
                                            >
                                                No Data Found
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* SIGNAL PIPELINE — full width */}
                    <div className="bg-white border rounded-4 shadow-sm p-4 mb-4">
                        <h3 className="fw-bold mb-2" style={{ fontSize: 18 }}>
                            Signal Pipeline
                        </h3>
                        <p className="text-secondary mb-3" style={{ fontSize: 13 }}>
                            Willingness versus completed actions across the active signals.
                        </p>
                        <div style={{ position: 'relative', height: 292 }}>
                            <canvas
                                ref={pipelineRef}
                                id="alumni-pipeline-chart"
                            />
                            {signalRowsByLevel.length === 0 && (
                                <div
                                    className="position-absolute top-50 start-50 translate-middle text-secondary"
                                    style={{ fontSize: 13 }}
                                >
                                    No Data Found
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SIGNAL-WISE BREAKDOWN */}
                    <div className="bg-white border rounded-4 shadow-sm p-4 mb-4">
                        <h3 className="fw-bold mb-3" style={{ fontSize: 18 }}>
                            Signal-wise Breakdown
                        </h3>
                        <div className="table-responsive">
                            <table
                                className="table align-middle mb-0"
                                style={{
                                    borderCollapse: 'separate',
                                    borderSpacing: '0 6px',
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th
                                            className="border-0 text-uppercase text-secondary"
                                            style={{ fontSize: 10, letterSpacing: '.06em' }}
                                        >
                                            Level
                                        </th>
                                        <th
                                            className="border-0 text-uppercase text-secondary"
                                            style={{ fontSize: 10, letterSpacing: '.06em' }}
                                        >
                                            Signal
                                        </th>
                                        <th
                                            className="border-0 text-center text-uppercase text-secondary"
                                            style={{ fontSize: 10, letterSpacing: '.06em' }}
                                        >
                                            Willing
                                        </th>
                                        <th
                                            className="border-0 text-center text-uppercase text-secondary"
                                            style={{ fontSize: 10, letterSpacing: '.06em' }}
                                        >
                                            Done
                                        </th>
                                        <th
                                            className="border-0 text-center text-uppercase text-secondary"
                                            style={{ fontSize: 10, letterSpacing: '.06em' }}
                                        >
                                            Total
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signalRowsByLevel.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="text-center text-muted py-3 border-0"
                                            >
                                                No data
                                            </td>
                                        </tr>
                                    )}
                                    {signalRowsByLevel.flatMap((group: any) =>
                                        group.rows.map((r: any, idx: number) => {
                                            return (
                                                <tr key={r.signalCode}>
                                                    {idx === 0 && (
                                                        <td
                                                            className="fw-bold border-0"
                                                            rowSpan={group.rows.length}
                                                            style={{
                                                                background: '#f8fafc',
                                                                borderRadius: '12px 0 0 12px',
                                                                color: primary,
                                                                fontSize: 12,
                                                                borderLeft: `3px solid ${primary}`,
                                                            }}
                                                        >
                                                            L{group.levelNumber}
                                                            <br />
                                                            <span className="fw-normal text-secondary">
                                                                {group.levelName}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td
                                                        className="border-0"
                                                        style={{
                                                            background: '#f8fafc',
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        {r.label}
                                                        {r.institute_tag === 'k12' && (
                                                            <span
                                                                className="badge rounded-pill ms-2"
                                                                style={{
                                                                    background: '#FFE0B2',
                                                                    color: '#E65100',
                                                                    fontSize: 10,
                                                                }}
                                                            >
                                                                K-12
                                                            </span>
                                                        )}
                                                        {r.institute_tag === 'college' && (
                                                            <span
                                                                className="badge rounded-pill ms-2"
                                                                style={{
                                                                    background: '#BBDEFB',
                                                                    color: '#0D47A1',
                                                                    fontSize: 10,
                                                                }}
                                                            >
                                                                COL
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td
                                                        className="text-center border-0"
                                                        style={{ background: '#f8fafc' }}
                                                    >
                                                        {/* Willing pill — goal accent (mid) on light bg */}
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: light,
                                                                color: primary,
                                                            }}
                                                        >
                                                            {r.willing}
                                                        </span>
                                                    </td>
                                                    <td
                                                        className="text-center border-0"
                                                        style={{ background: '#f8fafc' }}
                                                    >
                                                        {/* Done pill — goal primary on light bg */}
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: primary,
                                                                color: '#fff',
                                                            }}
                                                        >
                                                            {r.done}
                                                        </span>
                                                    </td>
                                                    <td
                                                        className="text-center fw-bold border-0"
                                                        style={{
                                                            background: '#f8fafc',
                                                            fontSize: 12,
                                                            borderRadius: '0 12px 12px 0',
                                                        }}
                                                    >
                                                        {r.total}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ALUMNI ENGAGEMENT LIST */}
                    <div className="bg-white border rounded-4 shadow-sm p-4">
                        <h3
                            className="fw-bold mb-3 d-flex align-items-center gap-2"
                            style={{ fontSize: 18 }}
                        >
                            {alumniListTitle}
                            <span
                                className="badge rounded-pill fw-normal"
                                style={{
                                    background: '#e2e8f0',
                                    color: '#475569',
                                    fontSize: 12,
                                }}
                            >
                                {filteredAlumni.length}
                            </span>
                        </h3>
                        <div className="table-responsive">
                            <table
                                className="table align-middle mb-0"
                                style={{
                                    borderCollapse: 'separate',
                                    borderSpacing: '0 6px',
                                }}
                            >
                                <thead>
                                    <tr>
                                        {['Name', 'Batch', 'Program', 'Level', 'Intensity', 'Signals'].map(
                                            (h) => (
                                                <th
                                                    key={h}
                                                    className="border-0 text-uppercase text-secondary"
                                                    style={{
                                                        fontSize: 10,
                                                        letterSpacing: '.06em',
                                                    }}
                                                >
                                                    {h}
                                                </th>
                                            )
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAlumni.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="text-center text-muted py-3 border-0"
                                            >
                                                No alumni match this view
                                            </td>
                                        </tr>
                                    )}
                                    {filteredAlumni.map((a: any) => {
                                        const lv = Number(a.assignedLevel) || 0;
                                        const intensity =
                                            Number(a.maxScore) > 0
                                                ? Math.round(
                                                      (Number(a.score) /
                                                          Number(a.maxScore)) *
                                                          100
                                                  )
                                                : 0;
                                        return (
                                            <tr
                                                key={a.userId}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    window.open(
                                                        // Use the alumni's own institute_id from the
                                                        // dashboard response — for group schools, the
                                                        // alumni's actual campus may differ from the
                                                        // page-level instituteId (the first selected
                                                        // campus in the dropdown). Fall back to the
                                                        // page-level id if the row doesn't carry one.
                                                        `/commitment/capture/${a.instituteId || instituteId}/${a.userId}`,
                                                        '_blank'
                                                    )
                                                }
                                            >
                                                <td
                                                    className="fw-bold border-0"
                                                    style={{
                                                        background: '#f8fafc',
                                                        borderRadius: '12px 0 0 12px',
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {a.name || a.userId}
                                                </td>
                                                <td
                                                    className="border-0"
                                                    style={{
                                                        background: '#f8fafc',
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {a.batch || '—'}
                                                </td>
                                                <td
                                                    className="border-0"
                                                    style={{
                                                        background: '#f8fafc',
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {/* Program — from user_institute_snapshot.program_name,
                                                        joined into the alumni rollup in getDashboard. */}
                                                    {(!isEmpty(a.programName) ? a.programName : '—')}
                                                </td>
                                                <td
                                                    className="border-0"
                                                    style={{ background: '#f8fafc' }}
                                                >
                                                    {lv === 0 ? (
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: '#e2e8f0',
                                                                color: '#64748b',
                                                            }}
                                                        >
                                                            U
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: primary,
                                                                color: '#fff',
                                                                opacity: Math.min(
                                                                    1,
                                                                    0.4 + 0.2 * lv
                                                                ),
                                                            }}
                                                        >
                                                            L{lv}
                                                        </span>
                                                    )}
                                                </td>
                                                <td
                                                    className="border-0"
                                                    style={{ background: '#f8fafc' }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="progress"
                                                            style={{
                                                                width: 70,
                                                                height: 9,
                                                                background: '#e2e8f0',
                                                            }}
                                                        >
                                                            <div
                                                                className="progress-bar rounded-pill"
                                                                style={{
                                                                    width: `${intensity}%`,
                                                                    background: primary,
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="text-secondary">
                                                            {intensity}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td
                                                    className="border-0"
                                                    style={{
                                                        background: '#f8fafc',
                                                        borderRadius: '0 12px 12px 0',
                                                    }}
                                                >
                                                    {/* 13×13 squares — one per signal in the active goal.
                                                        Color follows the alumnus's explicit Willing
                                                        answer (the source of intent); done_count
                                                        is shown in the tooltip, not the color. All
                                                        colors source from the active goal's flexCol:
                                                          willing = 'Yes' → primary (dark)
                                                          willing = 'No'  → mid     (medium/light)
                                                          else (done>0)   → primary (legacy: done implies yes)
                                                          no data         → #f1f5f9 with grey border */}
                                                    {goalSignalFlat.map((s: any) => {
                                                        const st =
                                                            userSignalsByUser[a.userId]?.[s.code];
                                                        let bg = '#f1f5f9';
                                                        let border = '1px solid #cbd5e1';
                                                        let status = 'No data';
                                                        if (st?.willing === 'Yes') {
                                                            bg = primary;
                                                            border = '1px solid transparent';
                                                            status =
                                                                st.doneCount > 0
                                                                    ? `Yes — Done ${st.doneCount}×`
                                                                    : 'Yes — Willing';
                                                        } else if (st?.willing === 'No') {
                                                            bg = mid;
                                                            border = '1px solid transparent';
                                                            status =
                                                                st.doneCount > 0
                                                                    ? `No (done ${st.doneCount}× earlier)`
                                                                    : 'No';
                                                        } else if (st?.doneCount > 0) {
                                                            bg = primary;
                                                            border = '1px solid transparent';
                                                            status = `Done ${st.doneCount}×`;
                                                        }
                                                        return (
                                                            <span
                                                                key={s.code}
                                                                className="d-inline-block me-1 rounded-1"
                                                                title={`${s.label} — ${status}`}
                                                                style={{
                                                                    width: 13,
                                                                    height: 13,
                                                                    background: bg,
                                                                    border,
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </SmartContext.Provider>
    );
};

export default CommitmentTracker;
